import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import client, { setSubscriptionExpiredHandler } from './api/client';
import ErrorBoundary from './components/ErrorBoundary';
import SubscriptionOverlay from './components/SubscriptionOverlay';
import BottomNav from './components/BottomNav/BottomNav';
import ToastHost from './components/Toast/ToastHost';
import { toast } from './components/Toast/toast';
import Catalog from './pages/Catalog/Catalog';
import Favorites from './pages/Favorites/Favorites';
import Cart from './pages/Cart/Cart';
import Profile from './pages/Profile/Profile';
import Checkout from './pages/Checkout/Checkout';
import { getPlatform } from './utils/platform';

// Админка
import NewAdminLayout from './pages/Admin/NewAdminLayout';
import Dashboard from './pages/Admin/Dashboard';
import ProductsAdmin from './pages/Admin/ProductsAdmin';
import ProductEditorAdmin from './pages/Admin/ProductEditorAdmin';
import OrdersAdmin from './pages/Admin/OrdersAdmin';
import TeamAdmin from './pages/Admin/TeamAdmin';
import UsersPage from './pages/Admin/UsersPage';
import MorePage from './pages/Admin/MorePage';
import StatsAdmin from './pages/Admin/StatsAdmin';
import SubscriptionAdmin from './pages/Admin/SubscriptionAdmin';
import ActivityLogPage from './pages/Admin/ActivityLogPage';
import BroadcastPage from './pages/Admin/BroadcastPage';
import SettingsPage from './pages/Admin/SettingsPage';
import FaqPage from './pages/Admin/FaqPage';
import SitePhotosAdmin from './pages/Admin/SitePhotosAdmin';
import NotesAdmin from './pages/Admin/NotesAdmin';
import CategoriesAdmin from './pages/Admin/CategoriesAdmin';

function ConditionalBottomNav() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <BottomNav />;
}

function AppRoutes() {
  const { subscriptionExpired, setSubscriptionExpired } = useApp();

  useEffect(() => {
    setSubscriptionExpiredHandler(() => setSubscriptionExpired(true));
  }, [setSubscriptionExpired]);

  return (
    <BrowserRouter>
      <AppContent subscriptionExpired={subscriptionExpired} />
    </BrowserRouter>
  );
}

function DeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const platform = getPlatform();
    let startParam = null;

    if (platform === 'telegram') {
      const tg = window.Telegram?.WebApp;
      startParam = tg?.initDataUnsafe?.start_param;
    } else if (platform === 'max') {
      startParam = window.WebApp?.initDataUnsafe?.start_param;
    }

    if (!startParam) {
      const urlParams = new URLSearchParams(window.location.search);
      startParam = urlParams.get('start');
    }

    const urlParams = new URLSearchParams(window.location.search);
    let broadcastId = urlParams.get('broadcast');
    // MAX не пробрасывает query-параметры в мини-апп — id рассылки вшит
    // в startapp payload (product_X_bcast_<id>); получаем его через start_param
    if (startParam && startParam.includes('_bcast_')) {
      const parts = startParam.split('_bcast_');
      if (!broadcastId) broadcastId = parts[1];
      startParam = parts[0];
    }

    // Трекинг клика по рассылке: отметка ставится ТОЛЬКО после успешной отправки,
    // при неудаче (например, пока нет токена авторизации) повторяем до 4 раз
    if (broadcastId && !sessionStorage.getItem(`tracked_${broadcastId}`)) {
      const tryTrack = (attempt) => {
        import('./api/client')
          .then(({ default: client }) => client.post(`/admin/broadcast/track/${broadcastId}`))
          .then(() => sessionStorage.setItem(`tracked_${broadcastId}`, '1'))
          .catch(() => {
            const delays = [1500, 4000, 9000];
            if (attempt < delays.length) {
              setTimeout(() => tryTrack(attempt + 1), delays[attempt]);
            }
          });
      };
      tryTrack(0);
    }

    if (startParam && startParam.startsWith('product_')) {
      const productId = startParam.replace('product_', '');
      // Не открываем повторно тот же товар
      if (sessionStorage.getItem('last_deep_link') === productId) return;
      sessionStorage.setItem('last_deep_link', productId);
      navigate(`/?product=${productId}`, { replace: true });
      return;
    }

    // Кнопка «💳 Оплатить» из уведомлений бота: мини-апп авторизован,
    // запрашиваем адрес оплаты и уходим на него
    if (startParam && startParam.startsWith('pay_')) {
      const orderId = startParam.replace('pay_', '');
      import('./api/client')
        .then(({ default: client }) => client.post(`/payment/resume/${orderId}`))
        .then(res => {
          const url = res.data?.confirmation_url;
          if (url) {
            window.location.href = url;
          } else {
            toast.info('Заказ уже оплачен.');
          }
        })
        .catch(err => {
          const detail = err.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'Не удалось открыть оплату. Попробуйте позже.');
        });
    }
  }, [navigate]);

  return null;
}

function AppContent({ subscriptionExpired }) {
  const location = useLocation();
  const { setSubscriptionExpired } = useApp();
  const isAdminPage = location.pathname.startsWith('/admin');

  // Оверлей поставлен 403-интерцептором (client.js) — один раз и навсегда.
  // При каждой смене страницы переспрашиваем сервер: продлили ли подписку.
  useEffect(() => {
    if (!subscriptionExpired) return;
    client.get('/subscription/status')
      .then(res => { if (res.data?.is_active) setSubscriptionExpired(false); })
      .catch(() => {});
  }, [subscriptionExpired, location.pathname, setSubscriptionExpired]);

  return (
    <>
      <DeepLinkHandler />
      {subscriptionExpired && !isAdminPage && <SubscriptionOverlay />}
      <div className={`mx-auto min-h-screen ${isAdminPage ? 'w-full bg-[#f1f5f9]' : 'max-w-md pb-24 bg-cream text-green-dark'}`}>
        <ErrorBoundary>
          <ToastHost />
          <Routes>
            {/* Основное приложение */}
            <Route path="/" element={<Catalog />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/profile" element={<Profile />} />

            {/* Админка */}
            <Route path="/admin" element={<NewAdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<ProductsAdmin />} />
              <Route path="products/new" element={<ProductEditorAdmin />} />
              <Route path="products/:id" element={<ProductEditorAdmin />} />
              <Route path="categories" element={<CategoriesAdmin />} />
              <Route path="orders" element={<OrdersAdmin />} />
              <Route path="team" element={<TeamAdmin />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="more" element={<MorePage />} />
              <Route path="stats" element={<StatsAdmin />} />
              <Route path="subscription" element={<SubscriptionAdmin />} />
              <Route path="activity" element={<ActivityLogPage />} />
              <Route path="broadcast" element={<BroadcastPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="faq" element={<FaqPage />} />
              <Route path="site-photos" element={<SitePhotosAdmin />} />
              <Route path="notes" element={<NotesAdmin />} />
            </Route>
          </Routes>
        </ErrorBoundary>
        <ConditionalBottomNav />
      </div>
    </>
  );
}

function App() {
  return (
    <AppProvider>
      <AppRoutes />
    </AppProvider>
  );
}

export default App;
